import { useState, useEffect } from "react";
import { X, Calendar } from "lucide-react";
import toast from "react-hot-toast";
import { applyParentLeave, getChildrenForLeave } from "@/services/parent";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fontSize, fontWeight, textColor } from "@/theme";

interface ChildLeaveItem {
  Child_Id: string;
  Child_Name: string;
  Subscription_Code: string;
}

const PUBLIC_HOLIDAYS = new Set([
  // 2026
  "2026-01-14", "2026-01-26", "2026-03-20", "2026-03-25", "2026-04-02",
  "2026-04-03", "2026-04-06", "2026-04-14", "2026-04-30", "2026-05-01",
  "2026-07-06", "2026-08-09", "2026-08-15", "2026-10-02", "2026-10-20",
  "2026-10-22", "2026-11-05", "2026-11-09", "2026-12-25",
  // 2027
  "2027-01-14", "2027-01-26", "2027-03-01", "2027-04-14", "2027-04-22",
  "2027-05-01", "2027-08-15", "2027-10-02", "2027-10-10", "2027-12-25",
]);

function isSunday(d: string) { return new Date(d).getDay() === 0; }
function isHoliday(d: string) { return PUBLIC_HOLIDAYS.has(d); }
function isBlockedDate(d: string) { return isSunday(d) || isHoliday(d); }

/** Returns all YYYY-MM-DD dates from start to end (inclusive) */
function getDatesInRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const current = new Date(from);
  const end = new Date(to);
  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

/** Parses the API response into a flat list of selectable children */
function parseChildrenResponse(data: any): ChildLeaveItem[] {
  const rawList: any[] = Array.isArray(data) ? data : data?.results ?? [];
  const result: ChildLeaveItem[] = [];

  rawList.forEach((item: any) => {
    if (item.Subscription_Code && Array.isArray(item.Child)) {
      // Nested: { Subscription_Code, Child: [{ Child_Id, Child_Name }] }
      item.Child.forEach((child: any) => {
        if (child.Child_Id) {
          result.push({
            Child_Id: child.Child_Id,
            Child_Name: child.Child_Name || "Unknown",
            Subscription_Code: item.Subscription_Code,
          });
        }
      });
    } else if (item.Child_Id && item.Subscription_Code) {
      // Flat: { Child_Id, Child_Name, Subscription_Code }
      result.push({
        Child_Id: item.Child_Id,
        Child_Name: item.Child_Name || "Unknown",
        Subscription_Code: item.Subscription_Code,
      });
    }
  });

  return result;
}

interface ApplyLeaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ApplyLeaveModal({ isOpen, onClose, onSuccess }: ApplyLeaveModalProps) {
  const userDetails = useCurrentUser();
  const username = userDetails?.username || "";

  const [children, setChildren] = useState<ChildLeaveItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [childrenError, setChildrenError] = useState("");
  const [dateError, setDateError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const loadChildren = async () => {
      setLoadingChildren(true);
      try {
        const response = await getChildrenForLeave(username);
        if (response.status === 200 && response.data) {
          setChildren(parseChildrenResponse(response.data));
        }
      } catch (error) {
        // console.error("Failed to load children:", error);
      } finally {
        setLoadingChildren(false);
      }
    };
    loadChildren();
  }, [isOpen]);

  const handleClose = () => {
    setSelectedIds([]);
    setFromDate("");
    setToDate("");
    setChildrenError("");
    setDateError("");
    onClose();
  };

  const toggleChild = (childId: string) => {
    setSelectedIds(prev =>
      prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]
    );
    setChildrenError("");
  };

  const handleFromDate = (value: string) => {
    if (isBlockedDate(value)) {
      setDateError(isSunday(value) ? "Sundays are not allowed" : "This is a public holiday and cannot be selected");
      setFromDate("");
      return;
    }
    setFromDate(value);
    setDateError("");
    if (toDate && value > toDate) setToDate("");
  };

  const handleToDate = (value: string) => {
    if (isBlockedDate(value)) {
      setDateError(isSunday(value) ? "Sundays are not allowed" : "This is a public holiday and cannot be selected");
      setToDate("");
      return;
    }
    setToDate(value);
    setDateError("");
  };

  const previewDays =
    fromDate && toDate && fromDate <= toDate
      ? getDatesInRange(fromDate, toDate).length
      : 0;

  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedIds.length === 0) {
      setChildrenError("Please select at least one child");
      return;
    }
    if (!fromDate || !toDate) {
      setDateError("Please select both From Date and To Date");
      return;
    }
    if (fromDate > toDate) {
      setDateError("From Date must be before or equal to To Date");
      return;
    }

    const selectedItems = children.filter(c => selectedIds.includes(c.Child_Id));
    const allChildIds = selectedItems.map(c => c.Child_Id);
    const subscriptionCode = selectedItems[0]?.Subscription_Code ?? "";
    const dateArray = getDatesInRange(fromDate, toDate).filter(d => !isBlockedDate(d));

    setSubmitting(true);
    try {
      const payload = {
        Username: username,
        Date: dateArray,
        Child_Id: allChildIds,
        Subscription_Code: subscriptionCode,
      };
      const response = await applyParentLeave(payload);
      if (response.status === 200 || response.status === 201) {
        toast.success("Leave request submitted successfully!");
        handleClose();
        onSuccess();
      } else {
        toast.error("Failed to submit leave request. Please try again.");
      }
    } catch (error) {
      console.error("Error applying leave:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 sticky top-0 z-10 rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-mlb-gold/20 border border-mlb-gold/30 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-mlb-gold" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Apply Leave Request</h2>
              <p className="text-xs text-white/60">Request delivery pause for your children</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Select Children */}
          <div>
            <label className={`block ${fontSize.sm} ${fontWeight.bold} ${textColor.heading} mb-2`}>
              Select Children <span className="text-red-500">*</span>
            </label>
            {loadingChildren ? (
              <div className="text-center py-6 text-gray-400 text-sm">Loading children…</div>
            ) : children.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
                No registered children found
              </div>
            ) : (
              <div className="space-y-2">
                {children.map((child) => {
                  const isSelected = selectedIds.includes(child.Child_Id);
                  return (
                    <label
                      key={child.Child_Id}
                      className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "border-brand-500 bg-brand-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleChild(child.Child_Id)}
                        className="w-4 h-4 text-brand-500 rounded accent-brand-500"
                      />
                      <p className={`${fontWeight.semibold} ${textColor.heading} text-sm`}>
                        {child.Child_Name}
                      </p>
                    </label>
                  );
                })}
              </div>
            )}
            {childrenError && <p className="text-red-500 text-xs mt-1.5">{childrenError}</p>}
          </div>

          {/* Date Range */}
          <div>
            <label className={`block ${fontSize.sm} ${fontWeight.bold} ${textColor.heading} mb-2`}>
              Leave Date Range <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className={`text-xs ${textColor.muted} mb-1.5 font-medium`}>From Date</p>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDate(e.target.value)}
                  min={today}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm text-gray-700 ${
                    dateError ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </div>
              <div>
                <p className={`text-xs ${textColor.muted} mb-1.5 font-medium`}>To Date</p>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => handleToDate(e.target.value)}
                  min={fromDate || today}
                  disabled={!fromDate}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 text-sm text-gray-700 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${
                    dateError ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </div>
            </div>

            {previewDays > 0 && (
              <div className="mt-2 flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
                <Calendar className="w-3.5 h-3.5 text-brand-500 flex-shrink-0" />
                <p className="text-xs text-brand-700 font-medium">
                  {previewDays} day{previewDays !== 1 ? "s" : ""} selected
                  {selectedIds.length > 0 && (
                    <span className="text-brand-500">
                      {" "}· {previewDays * selectedIds.length} total entries
                    </span>
                  )}
                </p>
              </div>
            )}

            {dateError && <p className="text-red-500 text-xs mt-1.5">{dateError}</p>}
            <p className="text-xs text-gray-400 mt-1.5">Sundays and public holidays are excluded from leave dates.</p>
          </div>

          {/* Info note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
            <div className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5">
              <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
            </div>
            <p className="text-amber-700 text-xs leading-relaxed">
              Leave requests can be cancelled before <strong>9 AM</strong> on the leave date.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={submitting || loadingChildren}
              className="flex-1 py-3 bg-mlb-gold hover:bg-mlb-gold/80 text-brand-700 rounded-xl transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
